import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Users, Gavel, DollarSign, ShoppingCart, Mail, 
  BarChart3, MessageSquare, Shield, GripVertical, Trophy, Tag, Smartphone, Truck, CreditCard, Package, Flag
} from 'lucide-react';

// Define all available tools - removed: Email Processor, Bulk Import, Import Tracking, Pending (badge on Orders tab), Live Activity (use Security > Live Monitor)
const DEFAULT_TOOLS = [
  {
    id: 'security',
    type: 'link',
    label: 'SECURITY',
    sublabel: 'TOOLS',
    icon: Shield,
    iconColor: 'text-amber-500',
    borderColor: 'hover:border-amber-500',
    borderBase: 'border-amber-500/30',
    to: '/admin/ip-intelligence',
    noStat: true,
  },
  {
    id: 'users',
    type: 'link',
    label: 'USERS',
    icon: Users,
    iconColor: 'text-tiffany-500',
    borderColor: 'hover:border-tiffany-500',
    to: '/admin/users',
    statKey: 'total_users',
  },
  {
    id: 'carts',
    type: 'link',
    label: 'USER CARTS',
    icon: ShoppingCart,
    iconColor: 'text-orange-500',
    borderColor: 'hover:border-orange-500',
    to: '/admin/carts',
    noStat: true,
  },
  {
    id: 'mailing',
    type: 'link',
    label: 'BLK LIST',
    icon: Mail,
    iconColor: 'text-purple-500',
    borderColor: 'hover:border-purple-500',
    to: '/admin/mailing-list',
    statKey: 'blk_list_count',
    altStatKey: 'mailing_list_count',
  },
  {
    id: 'blkcrd',
    type: 'link',
    label: 'BLKCRD',
    sublabel: 'MEMBERS',
    icon: CreditCard,
    iconColor: 'text-purple-400',
    borderColor: 'hover:border-purple-500',
    borderBase: 'border-purple-500/30',
    to: '/admin/blkcrd',
    statKey: 'blkcrd_members',
  },
  {
    id: 'auctions',
    type: 'link',
    label: 'AUCTIONS',
    icon: Gavel,
    iconColor: 'text-tiffany-500',
    borderColor: 'hover:border-tiffany-500',
    to: '/admin/bids',
    statKey: 'active_auctions',
    showNeedsReauction: true,
  },
  {
    id: 'analytics',
    type: 'link',
    label: 'ANALYTICS',
    sublabel: 'DASHBOARD',
    icon: BarChart3,
    iconColor: 'text-tiffany-500',
    borderColor: 'hover:border-tiffany-500',
    to: '/admin/analytics',
    noStat: true,
  },
  {
    id: 'promo-codes',
    type: 'link',
    label: 'PROMO',
    sublabel: 'CODES',
    icon: Tag,
    iconColor: 'text-purple-500',
    borderColor: 'hover:border-purple-500',
    to: '/admin/promo-codes',
    noStat: true,
  },
  {
    id: 'customers',
    type: 'link',
    label: 'TOP',
    sublabel: 'CUSTOMERS',
    icon: Trophy,
    iconColor: 'text-yellow-500',
    borderColor: 'hover:border-yellow-500',
    to: '/admin/customers',
    noStat: true,
  },
  {
    id: 'shipments',
    type: 'link',
    label: 'TRACK',
    sublabel: 'SHIPPING',
    icon: Truck,
    iconColor: 'text-blue-500',
    borderColor: 'hover:border-blue-500',
    to: '/admin/shipments',
    noStat: true,
  },
  {
    id: 'submissions',
    type: 'link',
    label: 'SUBMISSIONS',
    sublabel: 'SELL/TRADE',
    icon: Package,
    iconColor: 'text-emerald-500',
    borderColor: 'hover:border-emerald-500',
    borderBase: 'border-emerald-500/30',
    to: '/admin/submissions',
    statKey: 'pending_submissions',
    badgeColor: 'bg-emerald-500',
  },
  {
    id: 'profile-review',
    type: 'link',
    label: 'PROFILE',
    sublabel: 'REVIEW',
    icon: Flag,
    iconColor: 'text-red-500',
    borderColor: 'hover:border-red-500',
    borderBase: 'border-red-500/30',
    to: '/admin/profile-review',
    noStat: true,
  },
  {
    id: 'missing-cost',
    type: 'action',
    label: 'MISSING',
    sublabel: 'COST',
    icon: DollarSign,
    iconColor: 'text-red-500',
    borderColor: 'hover:border-red-500',
    borderBase: 'border-red-500/30',
    statKey: 'products_missing_cost',
    secondaryStatKey: 'orders_missing_cost',
    secondaryLabel: 'ORDERS',
    badgeColor: 'bg-red-500',
    actionTab: 'products',
    actionFilter: 'missing',
  },
  {
    id: '2fa',
    type: 'link',
    label: '2FA',
    sublabel: 'SECURITY',
    icon: Smartphone,
    iconColor: 'text-green-500',
    borderColor: 'hover:border-green-500',
    to: '/admin/2fa',
    noStat: true,
  },
  {
    id: 'revenue',
    type: 'link',
    label: 'REVENUE',
    icon: DollarSign,
    iconColor: 'text-green-500',
    borderColor: 'hover:border-green-500',
    to: '/admin/revenue',
    statKey: 'total_revenue',
    isCurrency: true,
    showConfirmed: true,
  },
  {
    id: 'support',
    type: 'link',
    label: 'SUPPORT',
    sublabel: 'MESSAGES',
    icon: MessageSquare,
    iconColor: 'text-tiffany-500',
    borderColor: 'hover:border-tiffany-500',
    to: '/admin/contact-messages',
    showUnread: true,
    noStat: true,
  },
  {
    id: 'costs',
    type: 'link',
    label: 'COSTS',
    sublabel: 'EXPENSES',
    icon: DollarSign,
    iconColor: 'text-emerald-500',
    borderColor: 'hover:border-emerald-500',
    to: '/admin/operating-costs',
    noStat: true,
  },
];

// Individual sortable tool card
function SortableToolCard({ tool, stats, unreadMessages, onTabChange, isDragging }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isItemDragging,
  } = useSortable({ id: tool.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = tool.icon;
  const statValue = tool.statKey ? (stats?.[tool.statKey] ?? stats?.[tool.altStatKey] ?? 0) : null;
  const secondaryStatValue = tool.secondaryStatKey ? (stats?.[tool.secondaryStatKey] ?? 0) : null;

  const cardContent = (
    <>
      {/* Drag handle */}
      <div 
        className="absolute top-1 right-1 p-1 text-zinc-600 hover:text-zinc-400 cursor-grab active:cursor-grabbing touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </div>
      
      <Icon className={`w-6 h-6 ${tool.iconColor} mb-2`} />
      
      {/* Stat or label */}
      {!tool.noStat && statValue !== null ? (
        <>
          <p className="font-mono text-xl sm:text-2xl text-white truncate">
            {tool.isCurrency ? `$${statValue.toFixed(0)}` : statValue}
            {secondaryStatValue !== null && (
              <span className="text-xs sm:text-sm text-zinc-500 ml-1 sm:ml-2">
                ({secondaryStatValue} {tool.secondaryLabel || ''})
              </span>
            )}
          </p>
          <p className="font-mono text-[10px] sm:text-xs text-zinc-500 uppercase truncate">{tool.label}</p>
        </>
      ) : (
        <>
          <p className="font-mono text-base sm:text-lg text-white uppercase truncate">{tool.label}</p>
          {tool.sublabel && <p className="font-mono text-[10px] sm:text-xs text-zinc-500 uppercase truncate">{tool.sublabel}</p>}
        </>
      )}
      
      {/* Needs reauction indicator */}
      {tool.showNeedsReauction && stats?.needs_reauction_count > 0 && (
        <div className="absolute top-2 right-8 flex items-center gap-1 bg-yellow-500/20 px-2 py-1">
          <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
          <span className="font-mono text-[10px] text-yellow-500">{stats.needs_reauction_count} NEED ACTION</span>
        </div>
      )}
      
      {/* Unread messages badge */}
      {tool.showUnread && unreadMessages > 0 && (
        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[24px] text-center animate-pulse">
          {unreadMessages}
        </span>
      )}
      
      {/* Pending submissions notification badge */}
      {tool.statKey === 'pending_submissions' && statValue > 0 && (
        <span className={`absolute -top-2 -right-2 ${tool.badgeColor || 'bg-emerald-500'} text-white text-xs font-bold px-2 py-1 rounded-full min-w-[24px] text-center animate-pulse`}>
          {statValue}
        </span>
      )}
    </>
  );

  const baseClasses = `border ${tool.borderBase || 'border-zinc-800'} p-4 text-left ${tool.borderColor} transition-colors relative h-[100px] ${
    isItemDragging ? 'opacity-50 z-50' : ''
  }`;

  if (tool.type === 'button') {
    return (
      <div ref={setNodeRef} style={style}>
        <button 
          onClick={() => onTabChange?.(tool.actionValue)}
          className={`${baseClasses} w-full`}
          data-testid={`tool-${tool.id}`}
        >
          {cardContent}
        </button>
      </div>
    );
  }

  if (tool.type === 'action') {
    return (
      <div ref={setNodeRef} style={style}>
        <button 
          onClick={() => onTabChange?.(tool.actionTab, tool.actionFilter)}
          className={`${baseClasses} w-full`}
          data-testid={`tool-${tool.id}`}
        >
          {cardContent}
        </button>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <Link 
        to={tool.to}
        className={`${baseClasses} block`}
        data-testid={`tool-${tool.id}`}
      >
        {cardContent}
      </Link>
    </div>
  );
}

// Drag overlay item (shown while dragging)
function DragOverlayCard({ tool, stats, unreadMessages }) {
  const Icon = tool.icon;
  const statValue = tool.statKey ? (stats?.[tool.statKey] ?? stats?.[tool.altStatKey] ?? 0) : null;

  return (
    <div className={`border ${tool.borderBase || 'border-zinc-800'} border-tiffany-500 p-4 text-left bg-zinc-900 shadow-xl relative`}>
      <Icon className={`w-6 h-6 ${tool.iconColor} mb-2`} />
      {!tool.noStat && statValue !== null ? (
        <>
          <p className="font-mono text-2xl text-white">
            {tool.isCurrency ? `$${statValue.toFixed(0)}` : statValue}
          </p>
          <p className="font-mono text-xs text-zinc-500">{tool.label}</p>
        </>
      ) : (
        <>
          <p className="font-mono text-lg text-white">{tool.label}</p>
          {tool.sublabel && <p className="font-mono text-xs text-zinc-500">{tool.sublabel}</p>}
        </>
      )}
    </div>
  );
}

export default function DraggableToolGrid({ 
  stats, 
  unreadMessages, 
  onTabChange,
  savedOrder,
  onOrderChange,
}) {
  const [toolOrder, setToolOrder] = useState(() => {
    // Initialize with saved order or default
    if (savedOrder && savedOrder.length > 0) {
      // Filter to only include valid tool IDs and maintain order
      const validIds = DEFAULT_TOOLS.map(t => t.id);
      const orderedTools = savedOrder
        .filter(id => validIds.includes(id))
        .map(id => DEFAULT_TOOLS.find(t => t.id === id))
        .filter(Boolean);
      
      // Add any new tools that weren't in saved order
      const savedIds = new Set(savedOrder);
      const newTools = DEFAULT_TOOLS.filter(t => !savedIds.has(t.id));
      
      return [...orderedTools, ...newTools];
    }
    return DEFAULT_TOOLS;
  });

  const [activeId, setActiveId] = useState(null);

  // Update when savedOrder changes
  useEffect(() => {
    if (savedOrder && savedOrder.length > 0) {
      const validIds = DEFAULT_TOOLS.map(t => t.id);
      const orderedTools = savedOrder
        .filter(id => validIds.includes(id))
        .map(id => DEFAULT_TOOLS.find(t => t.id === id))
        .filter(Boolean);
      
      const savedIds = new Set(savedOrder);
      const newTools = DEFAULT_TOOLS.filter(t => !savedIds.has(t.id));
      
      setToolOrder([...orderedTools, ...newTools]);
    }
  }, [savedOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const activeItem = useMemo(
    () => toolOrder.find(t => t.id === activeId),
    [activeId, toolOrder]
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setToolOrder((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        // Notify parent of order change
        onOrderChange?.(newOrder.map(t => t.id));
        
        return newOrder;
      });
    }
  };

  const toolIds = useMemo(() => toolOrder.map(t => t.id), [toolOrder]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={toolIds} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="stats-grid">
          {toolOrder.map((tool) => (
            <SortableToolCard
              key={tool.id}
              tool={tool}
              stats={stats}
              unreadMessages={unreadMessages}
              onTabChange={onTabChange}
              isDragging={activeId === tool.id}
            />
          ))}
        </div>
      </SortableContext>
      
      <DragOverlay>
        {activeItem ? (
          <DragOverlayCard 
            tool={activeItem} 
            stats={stats} 
            unreadMessages={unreadMessages}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
